import * as React from 'react';
import { CampusCardCheckbox } from '../../CampusCardCheckbox/CampusCardCheckbox';
import { AlertSectionVariant } from '~/Blocks/AlertSectionBlock/AlertSectionVariant.csharp';
import type { CampusCardBlockViewModel } from '~/Blocks/CampusCardBlock/ViewModel/CampusCardBlockViewModel.csharp';
import { CampusApplicationProcessState } from '~/Features/CampusApplication/CampusApplicationProcessState';
import { isItemAdded } from '~/Features/CampusApplication/Utils/campusApplicationUtils';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { ApplicationChoiceDto } from '~/Features/Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import { AlertSection } from '~/Partials/AlertSection/AlertSection';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { LazyCollapse } from '~/Partials/LazyCollapse/LazyCollapse';
import { List } from '~/Partials/List/List';
import { RadioGroupProvider } from '~/Partials/RadioGroup/RadioGroupContext';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { redirect } from '~/Partials/Utils/utils';
import type { Theme } from '~/Utils/Theme';
import styles from './AdmissionCallToActionCardsList.module.css';

interface AdmissionCallToActionCardsListProps {
    choices: ApplicationChoiceDto[];
    cards: CampusCardBlockViewModel[];
    submitButtonLabel: string;
    campusApplicationUrl: string;
    variantAlreadyInBasketAlertMessage: string;
    theme?: Theme;
}

export const AdmissionCallToActionCardsList: React.FC<AdmissionCallToActionCardsListProps> = props => {
    const contextualTheme = useTheme();
    const {
        cards,
        choices,
        campusApplicationUrl,
        submitButtonLabel,
        theme = contextualTheme,
        variantAlreadyInBasketAlertMessage,
    } = props;

    const [currentSelection, setCurrentSelection] = React.useState('');
    const [isVariantInCart, setIsVariantInCart] = React.useState(false);

    const processState = CampusApplicationProcessState();

    React.useEffect(() => {
        const selectedLocation = choices.find(
            choice => choice.variantCity.toLowerCase() === currentSelection.toLowerCase()
        );

        setIsVariantInCart(selectedLocation !== undefined && isItemAdded(selectedLocation.id, processState.getAll()));
    }, [currentSelection, choices]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const selectedLocation = choices.find(
            choice => choice.variantCity.toLowerCase() === currentSelection.toLowerCase()
        );

        if (!selectedLocation) {
            return;
        }

        const isSuccessfullyAdded = processState.append(selectedLocation);

        if (isSuccessfullyAdded) {
            redirect(campusApplicationUrl);

            return;
        }
    };

    const handleOnChange = (checkedValue: string) => {
        setCurrentSelection(checkedValue);
    };

    const picks = cards.map(card => ({ ...card, value: card.location }));

    return (
        <div>
            <form onSubmit={handleSubmit}>
                <RadioGroupProvider name="Card List" onChange={handleOnChange} currentValue={currentSelection}>
                    <List
                        className={styles.Picker}
                        items={picks}
                        renderItem={pick => (
                            <li className={styles.Pick} key={pick.title}>
                                <CampusCardCheckbox {...pick} />
                            </li>
                        )}
                    />
                    <LazyCollapse isOpen={isVariantInCart} className={styles.Alert}>
                        <AlertSection variant={AlertSectionVariant.Info}>
                            {variantAlreadyInBasketAlertMessage}
                        </AlertSection>
                    </LazyCollapse>

                    <Button
                        className={styles.Button}
                        htmltype="submit"
                        tier={ButtonTier.Secondary}
                        type={ButtonType.Solid}
                        size={ButtonSize.XLarge}
                        disabled={!currentSelection || isVariantInCart}
                        theme={theme}
                    >
                        <span>{submitButtonLabel}</span>
                        <SvgIcon iconName="arrow-right" />
                    </Button>
                </RadioGroupProvider>
            </form>
        </div>
    );
};
